import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowLeft, Clock } from 'lucide-react';
import { fetchOrder } from '../lib/api';

const EnrollmentSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder(orderId)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-[#5a5a5a]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-serif-besley text-3xl text-[#0F1B2D] mb-4">Application not found</h2>
        <Link to="/enroll-advocate" className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Try again
        </Link>
      </div>
    );
  }

  const paid = order.status === 'paid';
  const amountInr = (order.amount_paise || 0) / 100;

  return (
    <section className="min-h-[70vh] bg-[#F8F2E4] py-16" data-testid="enrollment-success-page">
      <div className="max-w-2xl mx-auto px-6">
        <div className="bg-white border border-[#E8DCC0] rounded-2xl p-10 text-center">
          {paid ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-[#E89530] mx-auto mb-5" strokeWidth={1.5} />
              <h1 className="font-serif-besley text-3xl md:text-4xl text-[#0F1B2D] mb-3">Application received</h1>
              <p className="text-[#3a3a3a] max-w-md mx-auto mb-4">
                Thank you, {order.customer_name.split(' ')[0]}. Your ₹{amountInr} platform fee has been paid
                successfully.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#E89530]/15 text-[#A36B1F] border border-[#E89530]/30 rounded-full px-4 py-1.5 text-sm mb-8">
                <Clock className="w-4 h-4" /> Under review by our verification team
              </div>
              <p className="text-sm text-[#5a5a5a] max-w-md mx-auto mb-8">
                We&apos;ll verify your Bar Council enrolment and get back to you within <b>3&ndash;5 working days</b> at{' '}
                <b>{order.customer_email}</b>. Once approved, your profile goes live on the public directory
                automatically.
              </p>
            </>
          ) : (
            <>
              <Clock className="w-16 h-16 text-[#A36B1F] mx-auto mb-5" strokeWidth={1.5} />
              <h1 className="font-serif-besley text-3xl md:text-4xl text-[#0F1B2D] mb-3">Payment status: {order.status}</h1>
              <p className="text-[#3a3a3a] mb-8">If the amount was debited, please email contact@lawforlayman.com with your reference number.</p>
            </>
          )}

          <div className="bg-[#EDE3CC] rounded-xl p-6 text-left space-y-2 mb-8">
            <div className="flex justify-between text-sm"><span className="text-[#5a5a5a]">Applicant</span><span className="font-medium text-[#0F1B2D]">{order.customer_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#5a5a5a]">Amount paid</span><span className="font-medium text-[#0F1B2D]">₹{amountInr}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#5a5a5a]">Reference</span><span className="font-mono text-xs text-[#0F1B2D]">{order.razorpay_payment_id || order.razorpay_order_id}</span></div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0F1B2D]/30 text-sm font-medium text-[#0F1B2D] hover:border-[#0F1B2D] transition-colors">
              Back to home
            </Link>
            <Link to="/find-a-lawyer" className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium">
              Explore the directory
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnrollmentSuccess;
